import React, { useCallback, useEffect, useRef, useState } from 'react';
import DsButton from './DsButton';
import {
  CURRENT_WEB_BUILD_ID,
  fetchLatestWebBuildId,
  isNewerWebBuild,
  nextVersionCheckRetryMs,
} from '../../utils/webAppVersion';
import '../../css/common/WebAppUpdateBanner.css';

const NORMAL_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MAX_RETRY_INTERVAL_MS = 60 * 60 * 1000;
const MIN_RESUME_CHECK_GAP_MS = 15 * 1000;
const DISMISSED_VERSION_KEY = 'casticart:web-update:dismissed-build';

const readDismissedBuild = (): string => {
  try {
    return String(window.sessionStorage.getItem(DISMISSED_VERSION_KEY) || '');
  } catch {
    return '';
  }
};

type Props = {
  currentBuildId?: string;
  loadLatestBuildId?: () => Promise<string>;
  reloadPage?: () => void;
  initialCheckDelayMs?: number;
  normalCheckIntervalMs?: number;
  random?: () => number;
};

export default function WebAppUpdateBanner({
  currentBuildId = CURRENT_WEB_BUILD_ID,
  loadLatestBuildId = fetchLatestWebBuildId,
  reloadPage = () => window.location.reload(),
  initialCheckDelayMs = 10_000,
  normalCheckIntervalMs = NORMAL_CHECK_INTERVAL_MS,
  random = Math.random,
}: Props) {
  const [availableBuildId, setAvailableBuildId] = useState('');
  const timerRef = useRef<number | null>(null);
  const checkingRef = useRef(false);
  const failureCountRef = useRef(0);
  const lastCheckedAtRef = useRef(0);
  const disposedRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const schedule = useCallback((delayMs: number, task: () => void) => {
    clearTimer();
    if (disposedRef.current || document.visibilityState === 'hidden') return;
    timerRef.current = window.setTimeout(task, Math.max(0, delayMs));
  }, [clearTimer]);

  const checkVersion = useCallback(async () => {
    if (
      disposedRef.current ||
      checkingRef.current ||
      document.visibilityState === 'hidden' ||
      !currentBuildId
    ) {
      return;
    }
    checkingRef.current = true;
    lastCheckedAtRef.current = Date.now();
    try {
      const latestBuildId = await loadLatestBuildId();
      failureCountRef.current = 0;
      if (isNewerWebBuild(currentBuildId, latestBuildId) && readDismissedBuild() !== latestBuildId) {
        setAvailableBuildId(latestBuildId);
      }
      schedule(normalCheckIntervalMs, checkVersion);
    } catch {
      failureCountRef.current += 1;
      const delay = nextVersionCheckRetryMs({
        failureCount: failureCountRef.current,
        baseMs: normalCheckIntervalMs,
        maxMs: MAX_RETRY_INTERVAL_MS,
        random,
      });
      schedule(delay, checkVersion);
    } finally {
      checkingRef.current = false;
    }
  }, [currentBuildId, loadLatestBuildId, normalCheckIntervalMs, random, schedule]);

  useEffect(() => {
    disposedRef.current = false;
    const initialJitterMs = Math.round(Math.min(Math.max(random(), 0), 1) * 5_000);
    schedule(initialCheckDelayMs + initialJitterMs, checkVersion);

    const checkAfterResume = () => {
      if (document.visibilityState === 'hidden') {
        clearTimer();
        return;
      }
      const elapsedMs = Date.now() - lastCheckedAtRef.current;
      if (elapsedMs >= MIN_RESUME_CHECK_GAP_MS) {
        clearTimer();
        void checkVersion();
      } else {
        schedule(MIN_RESUME_CHECK_GAP_MS - elapsedMs, checkVersion);
      }
    };
    window.addEventListener('focus', checkAfterResume);
    document.addEventListener('visibilitychange', checkAfterResume);
    return () => {
      disposedRef.current = true;
      clearTimer();
      window.removeEventListener('focus', checkAfterResume);
      document.removeEventListener('visibilitychange', checkAfterResume);
    };
  }, [checkVersion, clearTimer, initialCheckDelayMs, random, schedule]);

  if (!availableBuildId) return null;

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISSED_VERSION_KEY, availableBuildId);
    } catch {
      // sessionStorageを利用できない環境では、その場限りの非表示にする。
    }
    setAvailableBuildId('');
  };

  return (
    <section className="web-app-update-banner" role="status" aria-live="polite" aria-label="アプリの更新">
      <div className="web-app-update-banner__content">
        <strong>新しいバージョンを利用できます</strong>
        <span>入力中の内容を保存してから更新してください。</span>
      </div>
      <div className="web-app-update-banner__actions">
        <DsButton variant="secondary" size="sm" onClick={dismiss}>あとで</DsButton>
        <DsButton variant="primary" size="sm" onClick={reloadPage}>最新版に更新</DsButton>
      </div>
    </section>
  );
}
